import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader } from "@/components/common/Loader";
import { StatCard } from "@/components/common/StatCard";
import { LeadCard } from "@/components/common/LeadCard";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { formatINRShort } from "@/lib/format";
import {
  Plus, CalendarPlus, Sparkles, CalendarClock, Handshake, CheckCircle2,
  FileText, FileCheck2, IndianRupee, ScrollText, Car, TrendingUp,
} from "lucide-react";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/employee")
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader label="Loading your dashboard…" />;
  if (!data) return null;

  const s = data.stats;
  const m = data.monthly;
  const today = [
    { label: "Today's Leads", value: s.today_leads, icon: CalendarPlus, tone: "brand", to: "/leads" },
    { label: "New Leads", value: s.new_leads, icon: Sparkles, tone: "default", to: "/leads?status=NEW" },
    { label: "Follow-ups Today", value: s.followups_due_today, icon: CalendarClock, tone: "warning", to: "/leads?due=today" },
    { label: "Negotiations", value: s.negotiations, icon: Handshake, tone: "default", to: "/leads?status=NEGOTIATION" },
  ];
  const processing = [
    { label: "Deals Done", value: s.deals_done, icon: CheckCircle2, tone: "success", to: "/leads?status=DEAL_DONE" },
    { label: "Documents Pending", value: s.documents_pending, icon: FileText, tone: "warning", to: "/leads?deal=documents_pending" },
    { label: "Approval Pending", value: s.file_approval_pending, icon: FileCheck2, tone: "warning", to: "/leads?deal=approval_pending" },
    { label: "Payment Pending", value: s.payment_pending, icon: IndianRupee, tone: "danger", to: "/leads?deal=payment_pending" },
    { label: "COD Pending", value: s.cod_pending, icon: ScrollText, tone: "default", to: "/leads?deal=cod_pending" },
  ];

  return (
    <div className="animate-fade-in-up space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">{greeting()},</p>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">{user?.full_name?.split(" ")[0]}</h1>
        </div>
        <Button variant="brand" size="lg" className="hidden sm:flex" onClick={() => navigate("/leads/new")}>
          <Plus className="h-4 w-4" /> New Lead
        </Button>
      </div>

      {/* Monthly performance hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-navy p-5 shadow-elevated">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand/20 blur-2xl" />
        <div className="relative flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-foreground/70">
            <TrendingUp className="h-3.5 w-3.5" /> This month&rsquo;s performance
          </span>
        </div>
        <div className="relative mt-4 grid grid-cols-4 gap-2">
          {[
            { k: "Leads", v: m.leads_created },
            { k: "Deals", v: m.deals_done },
            { k: "Conv.", v: `${m.conversion}%` },
            { k: "Business", v: formatINRShort(m.business_amount) },
          ].map((x) => (
            <div key={x.k}>
              <div className="tnum font-display text-xl font-extrabold text-primary-foreground sm:text-2xl">{x.v}</div>
              <div className="text-[11px] text-primary-foreground/60">{x.k}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Today */}
      <section>
        <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Today</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {today.map((t) => (
            <StatCard key={t.label} {...t} onClick={() => navigate(t.to)} />
          ))}
        </div>
      </section>

      {/* Deal processing */}
      <section>
        <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Deal processing</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {processing.map((t) => (
            <StatCard key={t.label} {...t} onClick={() => navigate(t.to)} />
          ))}
        </div>
      </section>

      {/* Recent */}
      <section>
        <div className="mb-2.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground">Recently updated</h2>
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => navigate("/leads")}>View all</Button>
        </div>
        {data.recent_leads?.length ? (
          <div className="space-y-2.5">
            {data.recent_leads.map((l) => <LeadCard key={l.id} lead={l} />)}
          </div>
        ) : (
          <EmptyState icon={Car} title="No leads yet"
            description="Create your first vehicle lead to get started."
            action={<Button variant="brand" onClick={() => navigate("/leads/new")}><Plus className="h-4 w-4" /> New Lead</Button>} />
        )}
      </section>
    </div>
  );
}
